import { AlertCircle, ArrowUpDown, CreditCard, DollarSign, Loader2, Search, LifeBuoy } from 'lucide-react'
import { isTagadaPay } from '@/config/branding'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { CryptoPaymentHelper } from '@/components/ui/crypto-payment-helper'
import { Input } from '@/components/ui/input'
import { Separator } from '@/components/ui/separator'
import { Textarea } from '@/components/ui/textarea'
import { Switch } from '@/components/ui/switch'
import { useStoreNavigate } from '@/hooks/use-app-navigate'
import { useStoreRescueState } from '@/lib/api/cryptoRescue'
import { useIntegrationsList } from '@/lib/api/integrations'
import { usePaymentFlowWithProcessors } from '@/lib/api/paymentFlows'
import { cn } from '@/lib/utils'
import { getIntegrationImage } from '@/lib/utils/integration-utils'
import type { PaymentMethodEntry } from '../types'
import { startsEnabled } from '../utils/paymentMethodDefaults'
import { labelInputValue, labelWriteBack } from '../utils/paymentMethodLabel'
import { PaymentFlowSelector } from './PaymentFlowSelector'
import { PaymentMethodOrderModal } from './PaymentMethodOrderModal'

interface AvailableMethod {
  key: string
  label: string
  logoUrl: string
  description?: string
  source: 'card' | 'apm' | 'integration'
  processorId?: string
  processorName?: string
  integrationId?: string
  provider?: string
  supportedCountries?: string[]
  presentmentCurrencies?: string[]
  recurring?: boolean
  metadata?: Record<string, unknown>
  settings?: Record<string, unknown>
  /** Zelle: merchant payout email from integration credentials. Used to
   *  prefill the `{merchantEmail}` token in the step-config instructions and
   *  carried onto the entry so the renderer can interpolate it at runtime. */
  merchantEmail?: string
}

/** An express APM sub-method (apple_pay, google_pay, …) belonging to a processor group */
interface ExpressSubMethod {
  key: string   // raw apm key, e.g. "apple_pay"
  label: string
  logoUrl: string
}

/** One processor that has express APMs enabled */
interface ExpressGroup {
  processorId: string
  processorName: string
  publishableKey?: string
  methods: ExpressSubMethod[]
}

/** Key used in paymentSetupConfig for express groups */
const expressKey = (processorId: string) => `express_checkout:${processorId}`

/** The typed text is held locally: deriving the box from the saved config would
 *  blank it the instant what the merchant typed matched the default, and the
 *  rest of their keystrokes would land in an empty field. */
function MethodLabelInput({ label, help, stored, fallback, onCommit }: {
  label: string
  help: string
  stored: string
  fallback: string
  onCommit: (label: string) => void
}) {
  const [draft, setDraft] = useState(() => labelInputValue(stored, fallback))

  return (
    <div>
      <label className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">{label}</label>
      <Input
        className="mt-1 h-8 text-sm"
        value={draft}
        placeholder={fallback}
        onChange={(e) => {
          setDraft(e.target.value)
          onCommit(labelWriteBack(e.target.value, fallback))
        }}
      />
      <p className="mt-1 text-[10px] text-muted-foreground">{help}</p>
    </div>
  )
}

interface PaymentSetupConfigEditorProps {
  storeId: string
  paymentFlowId: string | undefined
  value: Record<string, PaymentMethodEntry> | undefined
  onChange: (config: Record<string, PaymentMethodEntry>) => void
  paymentFlowValue?: string
  onPaymentFlowChange?: (flowId: string | undefined) => void
}

const APM_PROVIDERS_TO_EXCLUDE = ['stripe_apm', 'airwallex_apm', 'tagadapay_apm']

/** Methods whose selector row owns a richer panel than the generic prompt
 *  line: card renders the card form, zelle / custom_payment render their
 *  instructions, hipay / whop take over the whole section, slash opens its own
 *  card form under its row. Underscore form — `rendersPromptPanel` normalizes
 *  before looking up. */
const NO_PROMPT_TYPES = new Set(['card', 'credit_card', 'zelle', 'custom_payment', 'hipay', 'whop', 'slash'])

/** Mirrors APM_LOGICAL_TYPES in the Studio selector Core: with a processorId
 *  these route through Stripe/Airwallex Elements (or the BLIK code field), so
 *  the prompt line is never rendered for them. Without one they are plain
 *  redirect methods and do show a prompt. */
const APM_PANEL_TYPES = new Set(['klarna', 'afterpay', 'skrill', 'bancontact', 'ideal', 'twint', 'blik', 'affirm'])

/** Default prompt copy the checkout falls back to when the merchant leaves
 *  the field empty — shown as the input placeholder so what they see here is
 *  what shoppers get. Keep in sync with DEFAULT_METHOD_PROMPTS in
 *  apps/studio-app/src/shared/commerce/PaymentMethodSelector/prompts.ts
 *  (separate app, no shared module). Anything absent here falls through to
 *  the selector's "You will be redirected to {method}…" template. */
const DEFAULT_PROMPT_TEXT: Record<string, string> = {
  apple_pay: 'Apple Pay will be prompted on your device to complete your payment.',
  google_pay: 'Google Pay will be prompted on your device to complete your payment.',
  paypal: 'You will be redirected to PayPal to complete your payment.',
  crypto: 'You will be redirected to our crypto payment service to complete your payment.',
  // On-chain crypto (inline panel; this prompt shows only if the panel cannot load). Not the Rampex
  // `crypto` on-ramp: no sub-provider list, the `=== 'crypto'` branches below leave it alone.
  crypto_onchain: 'Pay with crypto from your own wallet. The address and exact amount appear here.',
}

/** Native providers that can be placed in the checkout's express section, and
 *  the fallback used when neither the step's `express` flag nor the
 *  integration's `metadata.enableExpressCheckout` says anything.
 *
 *  The defaults differ on purpose:
 *   • paypal — express placement was shipped opt-in (TAG-1797); flipping it on
 *     by default would re-place PayPal on every published page.
 *   • apple_pay / google_pay — these have *always* rendered express whenever
 *     enabled, and most of their integrations predate the metadata flag, so it
 *     reads `undefined`. Defaulting them to false would silently strip the
 *     wallets from every existing store's express section.
 *
 *  Stripe ECE (`express_checkout:*`) is absent because it has no inline
 *  placement to toggle between — it is express-only by construction. */
const EXPRESS_CAPABLE_PROVIDERS: Record<string, { defaultExpress: boolean }> = {
  paypal: { defaultExpress: false },
  apple_pay: { defaultExpress: true },
  google_pay: { defaultExpress: true },
}

/** The renderer's method type in underscore form (`apple_pay`, `custom_payment`). */
const renderTypeOf = (method: AvailableMethod) =>
  (method.provider ?? method.key.split(':')[0]).replace(/-/g, '_')

/** True when this method's row renders the generic prompt line in the
 *  checkout selector — i.e. when a `promptText` would actually be shown. */
function rendersPromptPanel(method: AvailableMethod): boolean {
  if (method.source === 'card') return false
  const type = renderTypeOf(method)
  if (NO_PROMPT_TYPES.has(type)) return false
  if (method.processorId && APM_PANEL_TYPES.has(type)) return false
  return true
}

/**
 * The server switched this row off at the last funnel save because the step's published page is
 * older than the inline crypto panel (`hideCryptoOnchainWhereUnrenderable`). Without saying so the
 * merchant sees the switch flip back off after every save, with no way to tell why.
 */
function isHiddenByOutdatedPage(entry: PaymentMethodEntry | undefined): boolean {
  const native = entry?.metadata?._paymentFlowNative
  if (!native || typeof native !== 'object') return false
  return (native as { hiddenReason?: unknown }).hiddenReason === 'page_cannot_render_crypto_onchain'
}

/** Crypto providers hidden from UI but filtered out if previously activated */
const HIDDEN_CRYPTO_PROVIDERS = new Set(['cryptix', 'utorg', 'unlimit', 'guardarian', 'alchemypay', 'wert'])

/** Crypto on-ramp provider metadata — mirrors PROVIDERS in crypto-form.tsx */
const CRYPTO_PROVIDERS: { value: string; label: string; min: number; note?: string }[] = [
  { value: 'moonpay', label: 'MoonPay', min: 20 },
  { value: 'stripe', label: 'Stripe', min: 2, note: 'USD/USA only' },
  { value: 'rampnetwork', label: 'Ramp Network', min: 4, note: 'USD only' },
  { value: 'robinhood', label: 'Robinhood', min: 5, note: 'USD only' },
  { value: 'topper', label: 'Topper', min: 10 },
  { value: 'unlimit', label: 'Unlimit', min: 10 },
  { value: 'bitnovo', label: 'Bitnovo', min: 10 },
  { value: 'simpleswap', label: 'SimpleSwap', min: 10 },
  { value: 'mercuryo', label: 'Mercuryo', min: 15 },
  { value: 'revolut', label: 'Revolut', min: 15 },
  { value: 'transak', label: 'Transak', min: 15 },
  { value: 'cryptix', label: 'Cryptix', min: 15 },
  { value: 'guardarian', label: 'Guardarian', min: 20, note: 'EU-regulated' },
  { value: 'banxa', label: 'Banxa', min: 20 },
  { value: 'alchemypay', label: 'Alchemy Pay', min: 20 },
  { value: 'sardine', label: 'Sardine', min: 30 },
  { value: 'particle', label: 'Particle', min: 30 },
  { value: 'wert', label: 'Wert', min: 30 },
  { value: 'simplex', label: 'Simplex', min: 50 },
  { value: 'utorg', label: 'Utorg', min: 50 },
  { value: 'transfi', label: 'TransFi', min: 70, note: 'USD only' },
  { value: 'interac', label: 'Interac', min: 100, note: 'CAD only' },
  { value: 'upi', label: 'UPI', min: 100, note: 'INR only' },
]

/** Providers shown in the UI (hidden ones excluded) */
const VISIBLE_CRYPTO_PROVIDERS = CRYPTO_PROVIDERS.filter((p) => !HIDDEN_CRYPTO_PROVIDERS.has(p.value))

export function PaymentSetupConfigEditor({
  storeId,
  paymentFlowId,
  value,
  onChange,
  paymentFlowValue,
  onPaymentFlowChange,
}: PaymentSetupConfigEditorProps) {
  const { t } = useTranslation(['storefront', 'stores'])
  const { storeRoutes } = useStoreNavigate()
  const { data: flowData, isLoading: isLoadingFlow } = usePaymentFlowWithProcessors(
    paymentFlowId || '',
    !!paymentFlowId,
  )
  const { data: integrations, isLoading: isLoadingIntegrations } = useIntegrationsList(storeId)
  const { data: rescueState } = useStoreRescueState(storeId, !!storeId)
  // Gate the rescue banner on both `configured` (env is set) and `active` —
  // a stale `active` flag with no env shouldn't surface the locked state.
  const isRescueActive = rescueState?.configured === true && rescueState?.active === true
  const [reorderOpen, setReorderOpen] = useState(false)

  // Standard (non-express) available methods
  const availableMethods = useMemo(() => {
    const methods: AvailableMethod[] = []

    if (flowData) {
      methods.push({
        key: 'card',
        label: 'Card Payments',
        logoUrl: '/integrations/mastercard.svg',
        description: 'Accept credit and debit card payments',
        source: 'card',
      })

      const processors = flowData.allProcessors || []
      for (const processor of processors) {
        const options = processor.options as Record<string, unknown> | null
        const apmSettings = options?.apmSettings as Record<string, Record<string, unknown>> | undefined
        if (!apmSettings) continue

        for (const [apmKey, apmValue] of Object.entries(apmSettings)) {
          if (!apmValue.enabled) continue
          if (apmValue.isExpress === true) continue  // express methods go into expressGroups
          const countries = Array.isArray(apmValue.supportedCountries)
            ? (apmValue.supportedCountries as string[])
            : undefined
          const currencies = Array.isArray(apmValue.presentmentCurrencies)
            ? (apmValue.presentmentCurrencies as string[])
            : undefined
          methods.push({
            key: `${apmKey}:${processor.id}`,
            label: (apmValue.label as string) || apmKey,
            logoUrl: (apmValue.logoUrl as string) || getIntegrationImage(apmKey),
            description: (apmValue.description as string) || undefined,
            source: 'apm',
            processorId: processor.id,
            processorName: processor.name,
            supportedCountries: countries,
            presentmentCurrencies: currencies,
            recurring: typeof apmValue.recurring === 'boolean' ? apmValue.recurring : undefined,
          })
        }
      }
    }

    if (integrations) {
      for (const integration of integrations) {
        if (integration.type !== 'payment') continue
        if (!integration.enabled) continue
        if (APM_PROVIDERS_TO_EXCLUDE.includes(integration.provider)) continue
        methods.push({
          key: integration.provider,
          label: integration.name,
          logoUrl: integration.logoUrl || (integration.provider === 'custom_payment' ? '' : getIntegrationImage(integration.provider)),
          description: integration.description || undefined,
          source: 'integration',
          integrationId: integration.id,
          provider: integration.provider,
          metadata: (integration.metadata as Record<string, unknown>) || undefined,
          settings: (integration.settings as Record<string, unknown>) || undefined,
          merchantEmail:
            ((integration.credentials as Record<string, unknown> | null | undefined)?.merchantEmail as string | undefined) || undefined,
        })
      }
    }

    return methods
  }, [flowData, integrations])

  // Determine which crypto providers are pre-enabled from integration settings
  const integrationEnabledProviderIds = useMemo(() => {
    if (!integrations) return new Set<string>()
    const cryptoIntegration = integrations.find(
      (i) => i.type === 'payment' && i.provider === 'crypto' && i.enabled,
    )
    if (!cryptoIntegration) return new Set<string>()
    const settings = (cryptoIntegration.settings || {}) as {
      providers?: Array<{ id: string; enabled: boolean }>
      provider?: string
    }
    const enabledIds = new Set<string>()
    const providers = settings.providers?.filter((p) => p.enabled) || []
    for (const p of providers) enabledIds.add(p.id)
    // Fallback: if no providers array, use the single legacy provider field
    if (enabledIds.size === 0 && settings.provider) enabledIds.add(settings.provider)
    return enabledIds
  }, [integrations])

  // Express processor groups — one group per processor that has express APMs enabled
  const expressGroups = useMemo<ExpressGroup[]>(() => {
    if (!flowData) return []
    const groups: ExpressGroup[] = []

    for (const processor of flowData.allProcessors || []) {
      const options = processor.options as Record<string, unknown> | null
      const apmSettings = options?.apmSettings as Record<string, Record<string, unknown>> | undefined
      if (!apmSettings) continue

      const expressMethods: ExpressSubMethod[] = []
      for (const [apmKey, apmValue] of Object.entries(apmSettings)) {
        if (!apmValue.enabled) continue
        if (apmValue.isExpress !== true) continue
        expressMethods.push({
          key: apmKey,
          label: (apmValue.label as string) || apmKey,
          logoUrl: (apmValue.logoUrl as string) || getIntegrationImage(apmKey),
        })
      }

      if (expressMethods.length === 0) continue

      groups.push({
        processorId: processor.id,
        processorName: processor.name,
        publishableKey: (options?.expressPublishableKey as string | undefined) || undefined,
        methods: expressMethods,
      })
    }

    return groups
  }, [flowData])

  // Which processor is currently the active express processor.
  // Only honor keys whose processor is still part of THIS flow's expressGroups —
  // a stale `express_checkout:<oldId>` left behind by a payment-flow swap would
  // otherwise shadow the visible group, so its radio could never light up.
  const activeExpressProcessorId = useMemo(() => {
    if (!value) return null
    const groupIds = new Set(expressGroups.map((g) => g.processorId))
    for (const [key, entry] of Object.entries(value)) {
      if (key.startsWith('express_checkout:') && entry.enabled) {
        const id = key.slice('express_checkout:'.length)
        if (groupIds.has(id)) return id
      }
    }
    return null
  }, [value, expressGroups])

  const isEnabled = (method: AvailableMethod) => {
    const entry = value?.[method.key]
    if (!entry) return method.source === 'card'
    return entry.enabled
  }

  const toEntry = (method: AvailableMethod, enabled: boolean): PaymentMethodEntry => ({
    enabled,
    type: method.source,
    label: method.label,
    logoUrl: method.logoUrl,
    description: method.description,
    processorId: method.processorId,
    integrationId: method.integrationId,
    provider: method.provider,
    supportedCountries: method.supportedCountries,
    presentmentCurrencies: method.presentmentCurrencies,
    recurring: method.recurring,
    metadata: method.metadata,
    // Fold the Zelle payout email (held in integration credentials) into the
    // entry settings — credentials aren't part of the step config, so without
    // this the renderer's `{merchantEmail}` interpolation resolves to blank.
    settings: method.merchantEmail
      ? { ...(method.settings ?? {}), merchantEmail: method.merchantEmail }
      : method.settings,
  })

  // Ensure implicit defaults (like card) are materialized in the config object
  // so they aren't lost when other methods are toggled.
  const ensureDefaults = (config: Record<string, PaymentMethodEntry>): Record<string, PaymentMethodEntry> => {
    const result = { ...config }
    for (const method of availableMethods) {
      if (!(method.key in result) && method.source === 'card') {
        result[method.key] = toEntry(method, true)
      }
    }
    return result
  }

  // Ensure paymentSetupConfig is always populated so no checkout has 0 methods.
  // - If config already exists: re-emit as-is (preserves user choices)
  // - If config is empty/undefined: materialize from available methods + integrations
  const hasInitializedRef = useRef(false)
  useEffect(() => {
    if (hasInitializedRef.current) return
    if (isLoadingFlow || isLoadingIntegrations) return
    if (availableMethods.length === 0 && expressGroups.length === 0) return
    hasInitializedRef.current = true

    // Already has config — re-emit to ensure it's in the step config tree
    if (value && Object.keys(value).length > 0) {
      onChange({ ...value })
      return
    }

    // No config yet — build from available methods (all enabled by default, Slash aside)
    const initial: Record<string, PaymentMethodEntry> = {}
    for (const method of availableMethods) {
      initial[method.key] = toEntry(method, startsEnabled(method.provider))
    }
    for (const group of expressGroups) {
      const methods: Record<string, { enabled: boolean }> = {}
      for (const m of group.methods) methods[m.key] = { enabled: true }
      initial[expressKey(group.processorId)] = {
        enabled: true, type: 'apm', label: group.processorName,
        processorId: group.processorId, publishableKey: group.publishableKey, methods,
      }
    }
    onChange(initial)
  }, [isLoadingFlow, isLoadingIntegrations, availableMethods, expressGroups])

  // Crypto Rescue Mode banner — shown above every render variant of this
  // editor when the store is in rescue mode. The interactive content
  // below is also wrapped in a pointer-events-none container so the
  // merchant cannot touch any toggle while rescue is on. Deactivation
  // happens from the store Settings page Crypto Rescue Card.
  const rescueBanner = isRescueActive ? (
    <div className="rounded-md border-2 border-red-500/70 bg-red-50/30 px-3 py-2 text-sm text-red-700 dark:bg-red-950/10 dark:text-red-400">
      <div className="flex items-start gap-2">
        <LifeBuoy className="size-4 mt-0.5 shrink-0" />
        <span>
          {t('stores:paymentEditor.rescueBanner')}
        </span>
      </div>
    </div>
  ) : null

  if (!paymentFlowId) {
    return (
      <div className="space-y-3">
        {rescueBanner}
        <div className="flex items-center gap-2 rounded-md border border-dashed p-4 text-muted-foreground">
          <AlertCircle className="h-4 w-4 shrink-0" />
          <p className="text-sm">{t('paymentSetup.selectFlowFirst')}</p>
        </div>
      </div>
    )
  }

  if (isLoadingFlow || isLoadingIntegrations) {
    return (
      <div className="space-y-3">
        {rescueBanner}
        <div className="flex items-center justify-center py-6">
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        </div>
      </div>
    )
  }

  if (availableMethods.length === 0 && expressGroups.length === 0) {
    return (
      <div className="space-y-3">
        {rescueBanner}
        <div className="flex items-center gap-2 rounded-md border border-dashed p-4 text-muted-foreground">
          <AlertCircle className="h-4 w-4 shrink-0" />
          <p className="text-sm">
            {t('paymentSetup.noMethodsAvailable')}
          </p>
        </div>
      </div>
    )
  }

  /**
   * Render the checkout label + Description inputs for a given method key.
   * Mirrors the pattern used under crypto. Writes back to `value[key].label`
   * and `value[key].description`, preserving other fields (enabled/type/etc).
   */
  const renderLabelDescriptionInputs = (method: AvailableMethod) => {
    const entry = (value?.[method.key] as PaymentMethodEntry | undefined) ?? undefined
    const currentDescription = entry?.description ?? ''
    const fallbackType = entry?.type ?? method.source

    return (
      <div className='ml-4 mt-1 mb-2 border-l-2 border-border/50 pl-3 space-y-2'>
        <MethodLabelInput
          key={method.key}
          label={t('paymentSetup.checkoutLabel')}
          help={t('paymentSetup.checkoutLabelHelp')}
          stored={entry?.label ?? ''}
          fallback={method.label}
          onCommit={(label) => {
            onChange(ensureDefaults({
              ...(value ?? {}),
              [method.key]: {
                ...(entry ?? toEntry(method, true)),
                enabled: entry?.enabled ?? true,
                type: fallbackType,
                label,
              },
            }))
          }}
        />
        <div>
          <label className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">{t('paymentSetup.description')}</label>
          <Input
            className="mt-1 h-8 text-sm"
            value={currentDescription}
            placeholder={method.description || t('paymentSetup.descriptionPlaceholder')}
            onChange={(e) => {
              onChange(ensureDefaults({
                ...(value ?? {}),
                [method.key]: {
                  ...(entry ?? toEntry(method, true)),
                  enabled: entry?.enabled ?? true,
                  type: fallbackType,
                  label: entry?.label ?? method.label,
                  description: e.target.value,
                },
              }))
            }}
          />
        </div>
        {/* Free-text instructions panel, shown in the payment selector when
            this method is selected. Only Zelle and custom_payment render such
            a panel (ZelleInstructionsPanel / CustomPaymentInstructionsPanel);
            every other method shows a fixed/auto prompt, so the field is
            hidden for them. Storage key mirrors what the Studio renderer reads:
              • zelle          → settings.instructionsMessage (interpolates {merchantEmail}/{orderNumber})
              • custom_payment → settings.checkoutInstructions */}
        {renderPromptInput(method, entry, fallbackType)}
        {renderPromptTextInput(method, entry, fallbackType)}
        {renderCardFieldLabelInputs(method, entry, fallbackType)}
        {renderSlashFieldLabelInputs(method, entry, fallbackType)}
        {renderExpressToggle(method, entry, fallbackType)}
      </div>
    )
  }

  /** Step-level "show as express button" switch for the native wallets that
   *  the studio express section knows how to fire — see
   *  EXPRESS_CAPABLE_PROVIDERS. `!method.processorId` is the discriminator vs
   *  the APM variants (PayPal through Stripe/Airwallex, Apple/Google Pay
   *  through Stripe ECE): those route through Elements and never render in
   *  that section, so the toggle would be a lie for them.
   *
   *  Writes the entry's `express` tri-state: unset falls through to the
   *  integration's metadata.enableExpressCheckout and then to the provider's
   *  own default, an explicit value overrides both for this step in either
   *  direction. */
  const renderExpressToggle = (
    method: AvailableMethod,
    entry: PaymentMethodEntry | undefined,
    fallbackType: PaymentMethodEntry['type'],
  ) => {
    const spec = method.provider ? EXPRESS_CAPABLE_PROVIDERS[method.provider] : undefined
    if (!spec || method.processorId) return null

    // `?? spec.defaultExpress` rather than `=== true`: an Apple/Google Pay
    // integration saved before the express flag existed carries `undefined`,
    // and for those undefined means "yes, as it always has".
    const integrationDefault =
      (entry?.metadata?.enableExpressCheckout as boolean | undefined)
      ?? (method.metadata?.enableExpressCheckout as boolean | undefined)
      ?? spec.defaultExpress
    const checked = entry?.express ?? integrationDefault
    const methodName = entry?.label || method.label

    return (
      <div className="flex items-center justify-between gap-3 pt-1">
        <div className="min-w-0">
          <label className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
            {t('paymentSetup.showAsExpressButton')}
          </label>
          <p className="text-[10px] text-muted-foreground">
            {t('paymentSetup.showAsExpressButtonHelp', { method: methodName })}
          </p>
        </div>
        <Switch
          checked={checked}
          onCheckedChange={(enabled) => {
            onChange(ensureDefaults({
              ...(value ?? {}),
              [method.key]: {
                ...(entry ?? toEntry(method, true)),
                enabled: entry?.enabled ?? true,
                type: fallbackType,
                label: entry?.label ?? method.label,
                express: enabled,
              },
            }))
          }}
          className="shrink-0"
        />
      </div>
    )
  }

  /** The card row has no prompt line — selecting it opens the inline card
   *  form instead. These are that form's own field labels, the card-payment
   *  equivalent of the prompt text every other method gets. Written flat on
   *  the entry (not under `settings`) so the studio resolver can hand them
   *  to the selector the same way it does `description` / `promptText`.
   *
   *  Each label is what the shopper reads inside the input, so the
   *  placeholder here is the exact text the checkout shows when the field is
   *  left empty — `MM/YY` and `123` for expiry and CVC, which display a
   *  format hint rather than a word. Not run through `t()`: that copy is
   *  English on the page unless the merchant localizes it under the step's
   *  payment translations, and the CRM's display language says nothing
   *  about the shopper's. */
  const CARD_LABEL_FIELDS = [
    { key: 'cardNumberLabel', i18nKey: 'paymentSetup.cardNumberLabel', defaultLabel: 'Card Number Label', fallback: 'Card Number' },
    { key: 'expiryLabel', i18nKey: 'paymentSetup.expiryLabel', defaultLabel: 'Expiry Label', fallback: 'MM/YY' },
    { key: 'cvcLabel', i18nKey: 'paymentSetup.cvcLabel', defaultLabel: 'CVC Label', fallback: '123' },
  ] as const

  const renderCardFieldLabelInputs = (
    method: AvailableMethod,
    entry: PaymentMethodEntry | undefined,
    fallbackType: PaymentMethodEntry['type'],
  ) => {
    if (method.source !== 'card') return null

    return (
      <div className="space-y-2">
        {CARD_LABEL_FIELDS.map((field) => (
          <div key={field.key}>
            <label className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
              {t(field.i18nKey, { defaultValue: field.defaultLabel })}
            </label>
            <Input
              className="mt-1 h-8 text-sm"
              value={entry?.[field.key] ?? ''}
              placeholder={field.fallback}
              onChange={(e) => {
                const nextEntry: PaymentMethodEntry = {
                  ...(entry ?? toEntry(method, true)),
                  enabled: entry?.enabled ?? true,
                  type: fallbackType,
                  label: entry?.label ?? method.label,
                }
                nextEntry[field.key] = e.target.value === '' ? undefined : e.target.value
                onChange(ensureDefaults({ ...(value ?? {}), [method.key]: nextEntry }))
              }}
            />
          </div>
        ))}
        <p className="text-[10px] text-muted-foreground">
          {t('paymentSetup.cardFieldLabelsHelp')}
        </p>
      </div>
    )
  }

  /** Slash's card form asks for the buyer's name, street and postal code when
   *  the page did not collect them: Slash needs them to take the payment
   *  (#TAG-2955). Stored under the entry's `settings`, which the studio
   *  resolver hands to the selector with the rest of the method's metadata.
   *  Placeholders are the literal copy the checkout shows when a field is
   *  left empty, English unless the merchant localizes it — same reasoning as
   *  the card form labels above. */
  const SLASH_LABEL_FIELDS = [
    { key: 'nameLabel', i18nKey: 'paymentSetup.slashNameLabel', defaultLabel: 'Name Field Label', fallback: 'Full name' },
    { key: 'streetLabel', i18nKey: 'paymentSetup.slashStreetLabel', defaultLabel: 'Street Field Label', fallback: 'Street address' },
    { key: 'postalCodeLabel', i18nKey: 'paymentSetup.slashPostalCodeLabel', defaultLabel: 'Postal Code Field Label', fallback: 'Postal code' },
    { key: 'countryLabel', i18nKey: 'paymentSetup.slashCountryLabel', defaultLabel: 'Country Field Label', fallback: 'Country' },
  ] as const

  const renderSlashFieldLabelInputs = (
    method: AvailableMethod,
    entry: PaymentMethodEntry | undefined,
    fallbackType: PaymentMethodEntry['type'],
  ) => {
    if (renderTypeOf(method) !== 'slash') return null

    return (
      <div className="space-y-2">
        {SLASH_LABEL_FIELDS.map((field) => {
          const stored = entry?.settings?.[field.key]
          return (
            <div key={field.key}>
              <label className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
                {t(field.i18nKey, { defaultValue: field.defaultLabel })}
              </label>
              <Input
                className="mt-1 h-8 text-sm"
                value={typeof stored === 'string' ? stored : ''}
                placeholder={field.fallback}
                onChange={(e) => {
                  const base = entry ?? toEntry(method, startsEnabled(method.provider))
                  const settings = { ...(base.settings ?? {}), [field.key]: e.target.value === '' ? undefined : e.target.value }
                  onChange(ensureDefaults({
                    ...(value ?? {}),
                    [method.key]: { ...base, type: fallbackType, label: base.label ?? method.label, settings },
                  }))
                }}
              />
            </div>
          )
        })}
        <p className="text-[10px] text-muted-foreground">
          {t('paymentSetup.slashFieldLabelsHelp')}
        </p>
      </div>
    )
  }

  /** Single-line prompt shown under the method once the shopper selects it
   *  ("Apple Pay will be prompted on your device…"). Only rendered for the
   *  methods whose row actually shows that line — see `rendersPromptPanel`.
   *  Writes the top-level `promptText` the funnel schema already carries;
   *  the checkout falls back to the placeholder copy when it's empty. */
  const renderPromptTextInput = (
    method: AvailableMethod,
    entry: PaymentMethodEntry | undefined,
    fallbackType: PaymentMethodEntry['type'],
  ) => {
    if (!rendersPromptPanel(method)) return null

    const currentValue = entry?.promptText ?? ''
    const fallbackLabel = entry?.label || method.label
    // Not translated on purpose: this is the literal copy the checkout
    // renders when the field is empty, and that copy is English unless the
    // merchant localizes it under the step's payment translations. The CRM
    // display language says nothing about the shopper's.
    const placeholder = DEFAULT_PROMPT_TEXT[renderTypeOf(method)]
      ?? `You will be redirected to ${fallbackLabel} to complete your payment.`

    return (
      <div>
        <label className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
          {t('paymentSetup.promptText')}
        </label>
        <Input
          className="mt-1 h-8 text-sm"
          value={currentValue}
          placeholder={placeholder}
          onChange={(e) => {
            const next = e.target.value === '' ? undefined : e.target.value
            onChange(ensureDefaults({
              ...(value ?? {}),
              [method.key]: {
                ...(entry ?? toEntry(method, true)),
                enabled: entry?.enabled ?? true,
                type: fallbackType,
                label: entry?.label ?? method.label,
                promptText: next,
              },
            }))
          }}
        />
        <p className="mt-1 text-[10px] text-muted-foreground">
          {t('paymentSetup.promptTextHelp')}
        </p>
      </div>
    )
  }

  /** Multi-line instructions editor for Zelle / custom_payment — the only two
   *  methods whose selector panel renders editable free-text. Returns null for
   *  every other method. */
  const renderPromptInput = (
    method: AvailableMethod,
    entry: PaymentMethodEntry | undefined,
    fallbackType: PaymentMethodEntry['type'],
  ) => {
    const renderType = method.provider ?? method.key.split(':')[0]
    const isZelle = renderType === 'zelle'
    const settingsKey = isZelle ? 'instructionsMessage' : renderType === 'custom_payment' ? 'checkoutInstructions' : null
    if (!settingsKey) return null

    const currentSettings = (entry?.settings as Record<string, unknown> | undefined) ?? {}
    const merchantEmail = (currentSettings.merchantEmail as string | undefined) ?? method.merchantEmail
    const rawValue = (currentSettings[settingsKey] as string | undefined) ?? ''

    // Prefill the `{merchantEmail}` token with the integration's configured
    // payout email so the merchant edits the resolved address. `{orderNumber}`
    // is left untouched — it's resolved per order at checkout time.
    const currentValue = isZelle && merchantEmail
      ? rawValue.replace(/\{merchantEmail\}/g, merchantEmail)
      : rawValue

    const fieldLabel = t('paymentSetup.instructions')
    const helpText = t('paymentSetup.instructionsHelp')

    const writeValue = (val: string) => {
      const trimmed = val === '' ? undefined : val
      const base: PaymentMethodEntry = {
        ...(entry ?? toEntry(method, true)),
        enabled: entry?.enabled ?? true,
        type: fallbackType,
        label: entry?.label ?? method.label,
      }
      const nextSettings = { ...currentSettings }
      if (trimmed === undefined) delete nextSettings[settingsKey]
      else nextSettings[settingsKey] = trimmed
      // Persist the payout email alongside the copy so the renderer can
      // interpolate any `{merchantEmail}` token left in the text at runtime.
      if (isZelle && merchantEmail && !nextSettings.merchantEmail) nextSettings.merchantEmail = merchantEmail
      onChange(ensureDefaults({ ...(value ?? {}), [method.key]: { ...base, settings: nextSettings } }))
    }

    return (
      <div>
        <label className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">{fieldLabel}</label>
        <Textarea
          className="mt-1 min-h-16 text-sm"
          rows={4}
          value={currentValue}
          onChange={(e) => writeValue(e.target.value)}
        />
        <p className="mt-1 text-[10px] text-muted-foreground">{helpText}</p>
      </div>
    )
  }

  const handleToggle = (method: AvailableMethod, enabled: boolean) => {
    const existingOrder = value?.[method.key]?.order
    const entry = toEntry(method, enabled)
    if (existingOrder !== undefined) entry.order = existingOrder
    const updated = ensureDefaults({ ...value, [method.key]: entry })

    // When crypto is toggled on for the first time, seed providers from integration defaults
    if (method.provider === 'crypto' && enabled && !(value?.['crypto'] as any)?.providers) {
      const providers: Record<string, { enabled: boolean; label?: string; min?: number }> = {}
      for (const cp of CRYPTO_PROVIDERS) {
        if (HIDDEN_CRYPTO_PROVIDERS.has(cp.value)) continue
        providers[cp.value] = { enabled: integrationEnabledProviderIds.has(cp.value), label: cp.label, min: cp.min }
      }
      const firstEnabled = Object.entries(providers).find(([, v]) => v.enabled)?.[0]
      updated['crypto'] = {
        ...updated['crypto'],
        provider: firstEnabled || updated['crypto']?.provider,
        providers,
      } as any
    }

    onChange(updated)
  }

  // Build an express_checkout entry for a group
  const toExpressEntry = (group: ExpressGroup, enabled: boolean, methods: Record<string, { enabled: boolean }>): PaymentMethodEntry => ({
    enabled,
    type: 'apm',
    label: group.processorName,
    processorId: group.processorId,
    publishableKey: group.publishableKey,
    methods,
  })

  // Select a processor for express checkout — enables it, disables all others
  const handleExpressProcessorSelect = (processorId: string) => {
    const updated = { ...(value ?? {}) }
    const selectedKey = expressKey(processorId)

    // Disable every OTHER express entry — including stale ones whose processor is
    // no longer in this flow's expressGroups (e.g. left behind by a payment-flow
    // swap). Without this, a leftover enabled express_checkout:<oldId> keeps
    // winning activeExpressProcessorId and the visible radio never lights up.
    for (const key of Object.keys(updated)) {
      if (key.startsWith('express_checkout:') && key !== selectedKey && updated[key]?.enabled) {
        updated[key] = { ...updated[key], enabled: false }
      }
    }

    for (const group of expressGroups) {
      const key = expressKey(group.processorId)
      const isSelected = group.processorId === processorId

      if (isSelected) {
        // Restore existing per-method state or default all to enabled
        const existing = updated[key]
        const methods: Record<string, { enabled: boolean }> = {}
        for (const m of group.methods) {
          methods[m.key] = { enabled: existing?.methods?.[m.key]?.enabled ?? true }
        }
        updated[key] = toExpressEntry(group, true, methods)
      } else {
        // Disable this processor — preserve sub-method state for later restore
        const existing = updated[key]
        const methods: Record<string, { enabled: boolean }> = {}
        for (const m of group.methods) {
          methods[m.key] = { enabled: existing?.methods?.[m.key]?.enabled ?? true }
        }
        updated[key] = toExpressEntry(group, false, methods)
      }
    }

    onChange(ensureDefaults(updated))
  }

  // Toggle a single express sub-method within the active processor group
  const handleExpressMethodToggle = (group: ExpressGroup, methodKey: string, enabled: boolean) => {
    const key = expressKey(group.processorId)
    const existing = value?.[key]
    const methods = { ...(existing?.methods ?? {}) }
    for (const m of group.methods) {
      methods[m.key] = { enabled: methods[m.key]?.enabled ?? true }
    }
    methods[methodKey] = { enabled }
    onChange(ensureDefaults({ ...(value ?? {}), [key]: toExpressEntry(group, existing?.enabled ?? true, methods) }))
  }

  // Toggle a crypto sub-provider within the crypto integration entry
  const handleCryptoProviderToggle = (providerId: string, enabled: boolean) => {
    const existingEntry = value?.['crypto']
    const existingProviders = (existingEntry as any)?.providers as Record<string, { enabled: boolean; label?: string; min?: number }> | undefined
    const providers = { ...(existingProviders ?? {}) }

    // Strip hidden providers that may have been previously activated
    for (const hidden of HIDDEN_CRYPTO_PROVIDERS) {
      delete providers[hidden]
    }

    // Initialize visible crypto providers if not yet set
    for (const cp of VISIBLE_CRYPTO_PROVIDERS) {
      if (!providers[cp.value]) {
        providers[cp.value] = { enabled: integrationEnabledProviderIds.has(cp.value), label: cp.label, min: cp.min }
      }
    }
    providers[providerId] = { ...providers[providerId], enabled }

    // Always keep legacy "provider" field in sync with first enabled provider
    // so older plugin versions that only read "provider" still work
    const firstEnabled = Object.entries(providers).find(([, v]) => v.enabled)?.[0]
    onChange(ensureDefaults({
      ...(value ?? {}),
      crypto: {
        ...existingEntry,
        enabled: existingEntry?.enabled ?? true,
        type: 'integration' as const,
        label: existingEntry?.label || 'Crypto',
        provider: firstEnabled || existingEntry?.provider,
        providers,
      },
    }))
  }

  const isCryptoProviderEnabled = (providerId: string) => {
    const entry = value?.['crypto'] as any
    if (entry?.providers?.[providerId] !== undefined) {
      return entry.providers[providerId].enabled
    }
    // Fallback before initialization completes
    return integrationEnabledProviderIds.has(providerId)
  }

  const cardMethods = availableMethods.filter((m) => m.source === 'card')
  const apmMethods = availableMethods.filter((m) => m.source === 'apm')
  const integrationMethods = availableMethods.filter((m) => m.source === 'integration')

  const cryptoMethod = integrationMethods.find((m) => m.provider === 'crypto')
  const showCryptoHelper = !cryptoMethod || !isEnabled(cryptoMethod)

  const cryptoHasNoProviders = (() => {
    if (!cryptoMethod || !isEnabled(cryptoMethod)) return false
    const entry = value?.['crypto'] as any
    if (!entry?.providers) return false
    return !Object.values(entry.providers as Record<string, { enabled: boolean }>).some((v) => v.enabled)
  })()

  // Count enabled: standard methods + express groups that are enabled
  const enabledCount =
    availableMethods.filter((m) => isEnabled(m)).length +
    expressGroups.filter((g) => value?.[expressKey(g.processorId)]?.enabled).length

  const totalCount = availableMethods.length + expressGroups.length

  return (
    <div className="space-y-4">
      {rescueBanner}
      <div
        className={cn('space-y-4', isRescueActive && 'pointer-events-none opacity-60 select-none')}
        aria-disabled={isRescueActive}
      >
      <div className="flex items-center justify-between">
        <p className="text-xs text-muted-foreground">
          {t('paymentSetup.choosePaymentMethods')}
        </p>
        <Badge variant="secondary" className="text-[10px]">
          {t('paymentSetup.enabledCount', { enabled: enabledCount, total: totalCount })}
        </Badge>
      </div>

      <div className="space-y-1.5">
        {cardMethods.map((method) => (
          <div key={method.key}>
            <MethodRow
              method={method}
              enabled={isEnabled(method)}
              onToggle={(enabled) => handleToggle(method, enabled)}
            />
            {isEnabled(method) && renderLabelDescriptionInputs(method)}
          </div>
        ))}
        {cardMethods.some((m) => isEnabled(m)) && onPaymentFlowChange && (
          <div className="ml-11 mt-1.5">
            <PaymentFlowSelector
              storeId={storeId}
              value={paymentFlowValue}
              onChange={onPaymentFlowChange}
            />
          </div>
        )}
      </div>

      {apmMethods.length > 0 && (
        <>
          <Separator />
          <div className="space-y-1.5">
            <p className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground mb-2">
              {t('paymentSetup.alternativePaymentMethods')}
            </p>
            {apmMethods.map((method) => (
              <div key={method.key}>
                <MethodRow
                  method={method}
                  enabled={isEnabled(method)}
                  onToggle={(enabled) => handleToggle(method, enabled)}
                />
                {isEnabled(method) && renderLabelDescriptionInputs(method)}
              </div>
            ))}
          </div>
        </>
      )}

      {expressGroups.length > 0 && (
        <>
          <Separator />
          <div className="space-y-2">
            <p className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
              {t('paymentSetup.expressCheckout')}
            </p>
            {expressGroups.map((group) => {
              const key = expressKey(group.processorId)
              const isActive = activeExpressProcessorId === group.processorId
              const entry = value?.[key]

              return (
                <div
                  key={group.processorId}
                  className={cn(
                    'rounded-md border transition-colors',
                    isActive ? 'border-border bg-card' : 'border-transparent bg-muted/30',
                  )}
                >
                  {/* Processor selector row */}
                  <div
                    className="flex items-center gap-3 px-3 py-2 cursor-pointer"
                    onClick={() => handleExpressProcessorSelect(group.processorId)}
                  >
                    <div
                      className={cn(
                        'flex h-4 w-4 shrink-0 items-center justify-center rounded-full border-2',
                        isActive ? 'border-primary bg-primary' : 'border-muted-foreground',
                      )}
                    >
                      {isActive && <div className="h-1.5 w-1.5 rounded-full bg-white" />}
                    </div>
                    <span className={cn('text-sm font-medium flex-1', !isActive && 'text-muted-foreground')}>
                      {group.processorName}
                    </span>
                  </div>

                  {/* Individual method toggles — only visible when this group is active */}
                  {isActive && (
                    <div className="px-3 pb-2 space-y-1.5 border-t border-border/50 pt-2">
                      {group.methods.map((method) => {
                        const methodEnabled = entry?.methods?.[method.key]?.enabled ?? true
                        return (
                          <ExpressMethodRow
                            key={method.key}
                            method={method}
                            enabled={methodEnabled}
                            onToggle={(enabled) => handleExpressMethodToggle(group, method.key, enabled)}
                          />
                        )
                      })}
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        </>
      )}

      {integrationMethods.length > 0 && (
        <>
          <Separator />
          <div className="space-y-1.5">
            <p className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground mb-2">
              {t('paymentSetup.paymentIntegrations')}
            </p>
            {integrationMethods.map((method) => (
              <div key={method.key}>
                <MethodRow
                  method={method}
                  enabled={isEnabled(method)}
                  onToggle={(enabled) => handleToggle(method, enabled)}
                />
                {isEnabled(method) && renderLabelDescriptionInputs(method)}
                {!isEnabled(method) && isHiddenByOutdatedPage(value?.[method.key]) && (
                  <p className="ml-11 mt-1 flex items-start gap-1.5 text-xs text-muted-foreground">
                    <AlertCircle className="mt-0.5 h-3 w-3 shrink-0" />
                    {t('paymentSetup.cryptoOnchainOutdatedPage')}
                  </p>
                )}
                {/* Crypto sub-providers: show all providers when crypto is enabled */}
                {method.provider === 'crypto' && isEnabled(method) && (
                  <div className='ml-4 border-l-2 border-border/50 pl-3 pb-1'>
                    <CryptoProviderSelector
                      providers={VISIBLE_CRYPTO_PROVIDERS}
                      isProviderEnabled={isCryptoProviderEnabled}
                      onToggle={handleCryptoProviderToggle}
                    />
                    {cryptoHasNoProviders && (
                      <p className="ml-4 pl-3 text-xs text-destructive">
                        {t('paymentSetup.selectAtLeastOneProvider')}
                      </p>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        </>
      )}

      {isTagadaPay && showCryptoHelper && (
        <CryptoPaymentHelper to={storeRoutes.INTEGRATION_CONFIG('crypto')} />
      )}

      {enabledCount >= 2 && (
        <>
          <Separator />
          <Button
            variant="outline"
            size="sm"
            className="w-full h-7 text-xs gap-1.5"
            onClick={() => setReorderOpen(true)}
          >
            <ArrowUpDown className="h-3 w-3" />
            {t('paymentSetup.reorderPaymentMethods')}
          </Button>
        </>
      )}
      </div>

      {value && (
        <PaymentMethodOrderModal
          open={reorderOpen}
          onOpenChange={setReorderOpen}
          value={value}
          onChange={onChange}
        />
      )}

    </div>
  )
}

function MethodRow({
  method,
  enabled,
  onToggle,
}: {
  method: AvailableMethod
  enabled: boolean
  onToggle: (enabled: boolean) => void
}) {
  const { t } = useTranslation('storefront')
  const hasCurrencies = method.presentmentCurrencies && method.presentmentCurrencies.length > 0
  const hasCountries = method.supportedCountries && method.supportedCountries.length > 0

  return (
    <div
      className={cn(
        'flex items-center gap-3 rounded-md border px-3 py-2 transition-colors',
        enabled ? 'bg-card border-border' : 'bg-muted/30 border-transparent',
      )}
    >
      {method.logoUrl ? (
        <div
          className={cn(
            'flex h-8 w-8 shrink-0 items-center justify-center rounded-md border',
            enabled ? 'border-border bg-white' : 'border-transparent bg-muted/50',
          )}
        >
          <img
            src={method.logoUrl}
            alt={method.label}
            className={cn(
              'h-5 w-5 object-contain',
              !enabled && 'opacity-40 grayscale',
            )}
          />
        </div>
      ) : (
        <div
          className={cn(
            'flex h-8 w-8 shrink-0 items-center justify-center rounded-md',
            enabled ? 'bg-slate-500/10 text-slate-700' : 'bg-muted/50 text-muted-foreground/50',
          )}
        >
          <DollarSign className="h-4 w-4" />
        </div>
      )}

      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5">
          <span className={cn('text-sm font-medium', !enabled && 'text-muted-foreground')}>
            {method.label}
          </span>
          {method.source === 'card' && (
            <CreditCard className="h-3 w-3 text-muted-foreground" />
          )}
          {method.recurring === true && (
            <Badge variant="outline" className="text-[9px] px-1 py-0 leading-tight font-normal">
              {t('paymentSetup.recurring')}
            </Badge>
          )}
          {method.recurring === false && method.source === 'apm' && (
            <Badge variant="outline" className="text-[9px] px-1 py-0 leading-tight font-normal text-amber-600 border-amber-300">
              {t('paymentSetup.oneTime')}
            </Badge>
          )}
        </div>
        {method.processorName && (
          <p className="text-[10px] text-muted-foreground truncate">
            {t('paymentSetup.via', { name: method.processorName })}
          </p>
        )}
        {(hasCurrencies || hasCountries) && (
          <div className="flex items-center gap-1 mt-0.5 flex-wrap">
            {hasCurrencies && (
              <span className="text-[10px] font-mono text-muted-foreground">
                {method.presentmentCurrencies!.join(', ')}
              </span>
            )}
            {hasCurrencies && hasCountries && (
              <span className="text-[10px] text-muted-foreground">·</span>
            )}
            {hasCountries && (
              <span className="text-[10px] text-muted-foreground">
                {t('paymentSetup.countriesCount', { count: method.supportedCountries!.length })}
              </span>
            )}
          </div>
        )}
      </div>

      <Switch
        checked={enabled}
        onCheckedChange={onToggle}
        className="shrink-0"
      />
    </div>
  )
}

function ExpressMethodRow({
  method,
  enabled,
  onToggle,
}: {
  method: ExpressSubMethod
  enabled: boolean
  onToggle: (enabled: boolean) => void
}) {
  return (
    <div
      className={cn(
        'flex items-center gap-3 rounded-md border px-3 py-2 transition-colors',
        enabled ? 'bg-card border-border' : 'bg-muted/30 border-transparent',
      )}
    >
      <div
        className={cn(
          'flex h-8 w-8 shrink-0 items-center justify-center rounded-md border',
          enabled ? 'border-border bg-white' : 'border-transparent bg-muted/50',
        )}
      >
        <img
          src={method.logoUrl}
          alt={method.label}
          className={cn(
            'h-5 w-5 object-contain',
            !enabled && 'opacity-40 grayscale',
          )}
        />
      </div>

      <div className="min-w-0 flex-1">
        <span className={cn('text-sm font-medium', !enabled && 'text-muted-foreground')}>
          {method.label}
        </span>
      </div>

      <Switch
        checked={enabled}
        onCheckedChange={onToggle}
        className="shrink-0"
      />
    </div>
  )
}

function CryptoProviderSelector({
  providers,
  isProviderEnabled,
  onToggle,
}: {
  providers: typeof CRYPTO_PROVIDERS
  isProviderEnabled: (id: string) => boolean
  onToggle: (providerId: string, enabled: boolean) => void
}) {
  const { t } = useTranslation('storefront')
  const [search, setSearch] = useState('')

  const filtered = search
    ? providers.filter((p) =>
      p.label.toLowerCase().includes(search.toLowerCase()) ||
      p.value.toLowerCase().includes(search.toLowerCase()),
    )
    : providers

  return (
    <>
      <div className="relative mb-1.5">
        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
        <Input
          placeholder={t('paymentSetup.searchProviders')}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="h-7 pl-8 text-xs"
        />
      </div>
      <div className="max-h-52 overflow-y-auto space-y-0.5">
        {filtered.length === 0 && (
          <p className="text-xs text-muted-foreground text-center py-2">{t('paymentSetup.noProvidersFound')}</p>
        )}
        {filtered.map((cp) => {
          const enabled = isProviderEnabled(cp.value)
          return (
            <div
              key={cp.value}
              className={cn(
                'flex items-center gap-3 rounded-md px-3 py-1.5 transition-colors',
                enabled ? 'bg-card' : 'bg-muted/30',
              )}
            >
              <span className={cn('text-xs flex-1', !enabled && 'text-muted-foreground')}>
                {cp.label}
              </span>
              <span className="text-muted-foreground text-[10px]">min ${cp.min}</span>
              {cp.note && (
                <Badge variant="outline" className="text-[10px] shrink-0">{cp.note}</Badge>
              )}
              <Switch
                checked={enabled}
                onCheckedChange={(checked) => onToggle(cp.value, checked)}
                className="shrink-0 scale-75"
              />
            </div>
          )
        })}
      </div>
    </>
  )
}
